import clsx from "clsx";
import React, { useCallback, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import SpeedDial from "@material-ui/lab/SpeedDial";
import SpeedDialIcon from "@material-ui/lab/SpeedDialIcon";
import MoreHoriz from "@material-ui/icons/MoreHoriz";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import SpeedDialAction from "./SpeedDealAction";
import { noop } from "../../utils/function";
import { useKeyboardOpened } from "../../hooks/useKeyboardOpened";
import { isMobile } from "../../utils/helpers";
const FAB_LG_SIZE = 76.5;
const FAB_SM_SIZE = 68.2;
const FAB_FONT_SIZE_SM_SIZE = 8.9;
const FAB_FONT_SIZE_LG_SIZE = 10;
const BLUE_COLOR = "#71c4ea";
const TRANSLATE = isMobile ? 6.39375 : 7.17188;
const useStyles = makeStyles(theme => ({
  container: () => ({
    position: "fixed",
    bottom: theme.typography.pxToRem(10),
    right: 0,
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      bottom: theme.typography.pxToRem(40)
    },
    zIndex: theme.zIndex.speedDial
  }),
  speedDial: {
    position: "relative"
  },
  actions: ({
    fabSize
  }) => ({
    display: "flex",
    position: "absolute",
    alignItems: "center",
    width: theme.typography.pxToRem(fabSize * 4),
    height: theme.typography.pxToRem(fabSize * 4),
    borderRadius: "50%",
    bottom: theme.typography.pxToRem(-fabSize * 1.5),
    right: theme.typography.pxToRem(-fabSize * 1.5),
    willChange: "top, bottom, left, right, width, height"
  }),
  directionUp: {
    "& $actions": {
      marginBottom: 0,
      paddingBottom: 0
    }
  },
  fab: ({
    fabSize
  }) => ({
    width: theme.typography.pxToRem(fabSize),
    height: theme.typography.pxToRem(fabSize),
    transition: "0.15s",
    willChange: "transform"
  }),
  aroundFab: ({
    fabSize,
    fabFontSize
  }) => ({
    animationDuration: "0.2s",
    animationTimingFunction: "linear",
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: "50%",
    left: "50%",
    margin: theme.typography.pxToRem(-fabSize / 2),
    // half from width in action section
    border: `2px solid #FFF`,
    backgroundColor: "#000",
    color: "#FFF",
    fontSize: theme.typography.pxToRem(fabFontSize),
    fontWeight: 900,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: 1.2,
    letterSpacing: 0.16,
    textAlign: "center",
    padding: theme.typography.pxToRem(8)
  }),
  speedDealFab: ({
    fabSize
  }) => ({
    backgroundColor: `${BLUE_COLOR}!important`,
    width: theme.typography.pxToRem(fabSize),
    height: theme.typography.pxToRem(fabSize),
    zIndex: theme.zIndex.speedDial + 2
  }),
  speedDealButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  speedDealIcon: ({
    fabFontSize
  }) => ({
    fontSize: theme.typography.pxToRem(fabFontSize * 4),
    letterSpacing: 20
  }),
  hide: {
    display: "none"
  },
  mainIcon: {
    height: theme.typography.pxToRem(42),
    width: theme.typography.pxToRem(34)
  },

  /* ANIMATIONS FOR FAB COMPONENTS */
  "@keyframes slide0": {
    from: {
      transform: "translate(0)"
    },
    to: {
      transform: `rotate(180deg) translate(${TRANSLATE}rem) rotate(-180deg)`
    }
  },
  "@keyframes slide1": {
    from: {
      transform: "translate(0)"
    },
    to: {
      transform: `rotate(225deg) translate(${TRANSLATE}rem) rotate(-225deg)`
    }
  },
  "@keyframes slide2": {
    from: {
      transform: "translate(0)"
    },
    to: {
      transform: `rotate(270deg) translate(${TRANSLATE}rem) rotate(-270deg)`
    }
  },
  open0: {
    animationName: `$slide0`
  },
  open1: {
    animationName: `$slide1`
  },
  open2: {
    animationName: `$slide2`
  }
}));

const getTransform = (index, totalItems) => {
  const itemAngle = 90 / (totalItems - 1);
  const rotateAngle = 180 + index * itemAngle;
  return `rotate(${rotateAngle}deg) translate(${TRANSLATE}rem) rotate(-${rotateAngle}deg)`;
};

function FloatingActionMenu(props) {
  const {
    actions,
    width,
    onOpenClose,
    speedDialIconWithAction: [MainIcon, mainIconAction]
  } = props;
  const [isOpened, setOpen] = useState(false);
  useEffect(() => {
    setOpen(props.isMenuOpened);
  }, [props]);
  const openClose = React.useCallback(open => {
    setOpen(open);
    onOpenClose(open);
  }, [setOpen, onOpenClose]);
  const isLargeScreen = isWidthUp("sm", width);
  const fabSize = isLargeScreen ? FAB_LG_SIZE : FAB_SM_SIZE;
  const fabFontSize = isLargeScreen ? FAB_FONT_SIZE_LG_SIZE : FAB_FONT_SIZE_SM_SIZE;
  const classes = useStyles({
    fabSize,
    fabFontSize
  });
  const onClick = useCallback(index => {
    const action = actions[index];

    if (action && typeof action.callback === "function") {
      return action.callback();
    }

    return null;
  }, [actions]);
  const keyboardOpened = useKeyboardOpened();
  const icon = MainIcon ? /*#__PURE__*/React.createElement(MainIcon, {
    className: classes.mainIcon
  }) : /*#__PURE__*/React.createElement(SpeedDialIcon, {
    classes: {
      root: classes.speedDealButton
    },
    openIcon: /*#__PURE__*/React.createElement(MoreHoriz, {
      classes: {
        root: classes.speedDealIcon
      }
    })
  });
  return /*#__PURE__*/React.createElement("div", {
    className: clsx(classes.container, keyboardOpened && classes.hide)
  }, /*#__PURE__*/React.createElement(SpeedDial, {
    ariaLabel: "FloatingActionMenu",
    className: classes.speedDial,
    icon: icon,
    onClose: noop,
    onOpen: noop,
    open: isOpened,
    FabProps: {
      className: classes.speedDealFab,
      onClick: () => {
        mainIconAction ? mainIconAction() : openClose(!isOpened);
      }
    },
    direction: "up",
    classes: {
      directionUp: classes.directionUp,
      actions: classes.actions
    }
  }, actions.map((action, index) => /*#__PURE__*/React.createElement(SpeedDialAction, {
    FabProps: {
      style: {
        transform: getTransform(index, actions.length)
      },
      open: classes[`open${index}`]
    },
    classes: {
      fab: clsx(classes.fab, classes.aroundFab)
    },
    key: index,
    name: action.name,
    onClick: () => onClick(index)
  }))));
}

FloatingActionMenu.defaultProps = {
  onOpenClose: () => null
};
export default withWidth()(FloatingActionMenu);